// Agora RTC integration for CaratJewel Expo.
//
// Two flows served through one server-signed token endpoint:
//   - joinBroadcast(exhibitorId, roleHint)  -> live/host or live/audience
//   - joinMeeting(meetingId)                -> rtc/host (both parties publish)
//
// The App Certificate lives on the server. We only ever get short-lived tokens.

import AgoraRTC from "agora-rtc-sdk-ng";
import { api } from "@/api";

const APP_ID = process.env.REACT_APP_AGORA_APP_ID;

// Quieter default logs so buyers don't see Agora chatter.
AgoraRTC.setLogLevel(3);

export function isConfigured() {
  return Boolean(APP_ID);
}

export function boothChannel(exhibitorId) {
  return `booth-${exhibitorId}`;
}
export function meetingChannel(meetingId) {
  return `meeting-${meetingId}`;
}

async function fetchToken(flow, channel) {
  if (!APP_ID) throw new Error("Agora is not configured (REACT_APP_AGORA_APP_ID missing).");
  return api.agoraToken({ flow, channel });
}

/** Public-only remote state. Private meetings retain subscribeRemoteUsers below. */
export function subscribeBroadcastUsers(client, onUsersChange, hostUid) {
  let allowed = new Set(Array.isArray(hostUid) ? hostUid : [hostUid]);
  const users = new Map(), versions = new Map(), pending = new Map();
  let disposed = false;
  const emit = () => { if (!disposed) onUsersChange([...users.values()]); };
  const key = (user, type) => `${user.uid}:${type}`;
  const published = async (user, type) => {
    if (disposed || !allowed.has(user.uid) || !["audio", "video"].includes(type)) return;
    const id = key(user, type);
    if (pending.has(id) || users.get(user.uid)?.[`${type}Track`] === user[`${type}Track`] && user[`${type}Track`]) return;
    const generation = (versions.get(id) || 0) + 1;
    versions.set(id, generation); pending.set(id, generation);
    try {
      await client.subscribe(user, type);
      if (disposed || !allowed.has(user.uid) || versions.get(id) !== generation) return;
      const track = type === "video" ? user.videoTrack : user.audioTrack;
      const entry = { ...(users.get(user.uid) || { uid: user.uid }), [`${type}Track`]: track };
      users.set(user.uid, entry);
      if (type === "audio" && track) { try { track.play(); } catch {} }
      emit();
    } catch { /* A later event/reconciliation retries current published tracks. */ }
    finally { if (pending.get(id) === generation) pending.delete(id); }
  };
  const unpublished = (user, type) => {
    const id = key(user, type); versions.set(id, (versions.get(id) || 0) + 1); pending.delete(id);
    const entry = users.get(user.uid);
    if (entry) {
      try { entry[`${type}Track`]?.stop(); } catch {}
      entry[`${type}Track`] = null;
      if (!entry.audioTrack && !entry.videoTrack) users.delete(user.uid);
      emit();
    }
  };
  const left = (user) => { unpublished(user, "audio"); unpublished(user, "video"); };
  client.on("user-published", published); client.on("user-unpublished", unpublished); client.on("user-left", left);
  return {
    authorize(uids) {
      allowed = new Set(uids);
      for (const uid of users.keys()) if (!allowed.has(uid)) left({uid});
    },
    reconcile() {
      if (disposed) return;
      const current = client.remoteUsers || [];
      for (const uid of users.keys()) if (!current.some(u => u.uid === uid)) left({uid});
      for (const user of current) {
        if (user.hasAudio && !users.get(user.uid)?.audioTrack) published(user, "audio");
        if (user.hasVideo && !users.get(user.uid)?.videoTrack) published(user, "video");
      }
      emit();
    },
    clear() { for (const uid of new Set([...users.keys(), ...allowed])) left({uid}); },
    dispose() {
      disposed = true;
      client.off("user-published", published); client.off("user-unpublished", unpublished); client.off("user-left", left);
      for (const user of users.values()) for (const type of ["audioTrack", "videoTrack"]) { try { user[type]?.stop(); } catch {} }
      users.clear(); pending.clear();
    },
  };
}

/** One public session per component. No cross-account session cache. */
export async function joinBroadcast({ exhibitorId, exhibitionId, asHost, additionalCamera = false, signal, onState = () => {}, onUsers = () => {}, onError = () => {} }) {
  let reservation, auth, client, remote, tracks = [], joined = false, published = false;
  let stopped = false, ended = false, timer, leaseTimer, renewing, expiredDuringRenewal = false;
  let network = "CONNECTING", mediaConnected = true, deadline = 0;
  const presenceDevice = crypto.randomUUID();
  let presenceTimer, presenceController;
  const presencePayload = () => ({exhibitor_id: exhibitorId, session_id: reservation.session_id, device_id: presenceDevice, uid: auth.uid, renewal_key: auth.renewal_key});
  const presenceTick = async () => {
    if (stopped || asHost) return;
    if (joined && network === "CONNECTED" && mediaConnected) {
      presenceController = new AbortController();
      const timeout = setTimeout(() => presenceController?.abort(), 5000);
      try { await api.communication("/live-presence", presencePayload(), {signal: presenceController.signal}); } catch { /* Presence never controls media. */ }
      finally {clearTimeout(timeout);}
    }
    if (!stopped) presenceTimer = setTimeout(presenceTick, 15000);
  };
  const closedTracks = new Set(), trackListeners = new Map();
  const state = (value) => { if (!stopped && !signal?.aborted) onState(value); };
  const check = () => { if (stopped || signal?.aborted) throw new DOMException("Broadcast cancelled", "AbortError"); };
  const command = () => ({ exhibitor_id: exhibitorId, session_id: reservation.session_id, host_key: reservation.host_key, ...(reservation.camera_slot ? {camera_slot: reservation.camera_slot} : {}) });
  const release = async () => {
    clearTimeout(presenceTimer); presenceController?.abort();
    if (!asHost && reservation && auth) {try {api.communication("/live-presence/leave", presencePayload(), {keepalive:true}).catch(() => {});} catch {}}
    stopped = true; clearTimeout(timer); clearTimeout(leaseTimer); remote?.dispose();
    signal?.removeEventListener("abort", abort);
    globalThis.removeEventListener?.("pagehide", abort);
    // Close hardware immediately, including tracks created after an earlier abort.
    for (const track of tracks) if (!closedTracks.has(track)) {
      closedTracks.add(track); track.off?.("track-ended", trackListeners.get(track));
      try { track.stop(); } catch {} try { track.close(); } catch {}
    }
    if (client) {
      client.removeAllListeners();
      if (published) { published = false; try { await client.unpublish(tracks); } catch {} }
      // A rejected join can still leave SDK resources allocated. Leaving is safe
      // before join completion; a late completion runs this cleanup again.
      joined = false; try { await client.leave(); } catch {}
    }
    if (asHost && reservation && !ended) {
      ended = true;
      try { await api.endBroadcast(command()); }
      catch (err) { if (![403, 409].includes(err.status)) throw new Error("Media stopped. Server confirmation failed; the broadcast will expire automatically."); }
    }
  };
  const abort = () => { release().catch(() => {}); };
  const fail = async (err, terminal = "error") => {
    if (stopped) return;
    state(terminal); onError(humanizePermissionError(err));
    await release().catch(() => {});
  };
  // An API request can hang during network loss. Expiry must not depend on
  // that request returning before we stop media and clear the LIVE state.
  const localDeadline = value => Date.now() + (Math.min(value.expires_at, value.window_closes_at || Infinity) - (value.server_time ?? Date.now()/1000)) * 1000;
  const watchLease = () => {
    clearTimeout(leaseTimer);
    leaseTimer = setTimeout(() => fail(new Error("Public LIVE ended or the connection expired."), reservation.window_closes_at && reservation.expires_at >= reservation.window_closes_at ? "ended" : "unavailable"), Math.max(0, deadline - Date.now()));
  };
  signal?.addEventListener("abort", abort, {once: true});
  globalThis.addEventListener?.("pagehide", abort);
  try {
    check();
    if (!isConfigured()) throw new Error("Video service is not configured.");
    state("connecting");
    reservation = asHost
      ? additionalCamera
        ? await api.communication('/me/broadcast/camera/start', {exhibitor_id: exhibitorId, exhibition_id: exhibitionId, request_id: crypto.randomUUID()})
        : await api.startBroadcast({ exhibitor_id: exhibitorId, exhibition_id: exhibitionId, request_id: crypto.randomUUID() })
      : await api.broadcastStatus(exhibitorId);
    check();
    if (!asHost && !reservation.live) throw Object.assign(new Error("Broadcast ended or unavailable"), {status:409});
    deadline = localDeadline(reservation); watchLease();
    const payload = () => ({ flow: "broadcast", exhibitor_id: exhibitorId, session_id: reservation.session_id,
      channel: reservation.channel, ...(asHost ? {host_key: reservation.host_key, ...(reservation.camera_slot ? {camera_slot: reservation.camera_slot} : {})} : {}) });
    auth = await api.agoraToken(payload()); check();
    if (auth.role !== (asHost ? "host" : "audience") || auth.channel !== reservation.channel || auth.session_id !== reservation.session_id) throw new Error("Invalid broadcast authorization");
    client = AgoraRTC.createClient({mode: "live", codec: "vp8"});
    remote = subscribeBroadcastUsers(client, users => {
      if (stopped) return;
      onUsers(users);
      if (!asHost && network === "CONNECTED" && mediaConnected) state(users.some(u => u.videoTrack || u.audioTrack) ? "live" : "reconnecting");
    }, reservation.host_uids || [reservation.host_uid]);
    const recover = (expired = false) => {
      if (stopped) return;
      // A token already covering the daily close cannot be renewed further.
      // Avoid a renewal loop when the SDK's warning arrives in the final 30s.
      if (reservation.window_closes_at && auth.expires_at >= reservation.window_closes_at) {
        if (expired) return fail(new Error("Exhibition LIVE hours have ended"), "ended");
        return;
      }
      if (renewing) { expiredDuringRenewal ||= expired; return renewing; }
      renewing = (async () => {
        const next = await api.agoraToken({...payload(), uid: auth.uid, renewal_key: auth.renewal_key}); check();
        if (next.uid !== auth.uid || next.channel !== auth.channel || next.role !== auth.role) throw new Error("Invalid renewed broadcast authorization");
        if (expired) {
          state("reconnecting"); remote.clear();
          if (joined) { await client.leave(); joined = false; check(); }
          await client.join(next.app_id, next.channel, next.token, next.uid); joined = true; check();
          if (asHost) { await client.publish(tracks); published = true; check(); }
          remote.reconcile();
        } else { await client.renewToken(next.token); check(); }
        auth = next;
        if (asHost && network === "CONNECTED" && mediaConnected) state("live");
      })().catch(err => stopped ? release().catch(() => {}) : fail(err, [401,403,404,409].includes(err.status) ? "unavailable" : "error"))
        .finally(() => {
          renewing = null;
          if (expiredDuringRenewal && !stopped) { expiredDuringRenewal = false; recover(true); }
        });
      return renewing;
    };
    client.on("token-privilege-will-expire", () => recover(false));
    client.on("token-privilege-did-expire", () => recover(true));
    client.on("connection-state-change", current => {
      network = current;
      if (stopped) return;
      if (current === "CONNECTED") { remote.reconcile(); if (asHost && published && mediaConnected) state("live"); }
      else { state("reconnecting"); if (!asHost) remote.clear(); }
    });
    client.on("media-reconnect-start", () => { mediaConnected = false; state("reconnecting"); });
    client.on("media-reconnect-end", () => { mediaConnected = true; remote.reconcile(); if (asHost && published && network === "CONNECTED") state("live"); });
    await client.setClientRole(asHost ? "host" : "audience"); check();
    await client.join(auth.app_id, auth.channel, auth.token, auth.uid); joined = true; network = "CONNECTED"; check();
    if (!asHost) presenceTick();
    if (asHost) {
      tracks = await AgoraRTC.createMicrophoneAndCameraTracks({AEC: true, ANS: true}, {encoderConfig: "480p_1"}); check();
      for (const track of tracks) {
        const ended = () => fail(new Error("Camera or microphone disconnected. Start again when ready."));
        trackListeners.set(track, ended); track.on?.("track-ended", ended);
      }
      await client.publish(tracks); published = true; check();
      reservation = {...reservation, ...await api.publishBroadcast(command())}; check();
      deadline = localDeadline(reservation); watchLease(); state("live");
    }
    remote.reconcile();
    const tick = async () => {
      if (stopped) return;
      try {
        const next = asHost && network === "CONNECTED" && mediaConnected
          ? await api.heartbeatBroadcast(command()) : await api.broadcastStatus(exhibitorId);
        if (stopped) return;
        if (next.session_id !== reservation.session_id || !next.live) {
          await fail(new Error("Broadcast ended or unavailable"), "ended"); return;
        }
        // A viewer's own broken media connection must not wait forever on a healthy host.
        if (network === "CONNECTED" && mediaConnected) { reservation = {...reservation, ...next}; deadline = localDeadline(next); watchLease(); }
        if (!asHost) remote.authorize(next.host_uids || [next.host_uid]);
        remote.reconcile();
        if (asHost && network === "CONNECTED" && mediaConnected) state("live");
      } catch (err) {
        if ([401,403,404,409].includes(err.status)) { await fail(err, "unavailable"); return; }
        state("reconnecting");
      }
      if (Date.now() >= deadline) { await fail(new Error("Connection lost. Please try again."), "unavailable"); return; }
      if (!stopped) timer = setTimeout(tick, reservation.heartbeat_seconds * 1000);
    };
    timer = setTimeout(tick, reservation.heartbeat_seconds * 1000);
    return { client, localTracks: asHost ? tracks : null, role: auth.role, leave: release, sessionId: reservation.session_id,
      playAudio() { for (const user of client.remoteUsers) { try { user.audioTrack?.play(); } catch {} } },
    };
  } catch (err) {
    await release().catch(() => {});
    throw err;
  }
}

/**
 * Private 1-to-1 meeting. Both sides publish.
 */
export async function joinMeeting({ channel, signal, onUsers = () => {}, onState = () => {}, onError = () => {} }) {
  let auth, client, localTracks = [], remote, published = false, stopped = false;
  let timer, expiryTimer, renewal, expiredPending = false;
  const closed = new Set();
  const check = () => { if (stopped || signal?.aborted) throw new DOMException("Meeting cancelled", "AbortError"); };
  const state = value => { if (!stopped && !signal?.aborted) onState(value); };
  const leave = async () => {
    stopped = true; clearTimeout(timer); clearTimeout(expiryTimer); remote?.();
    signal?.removeEventListener("abort", abort); globalThis.removeEventListener?.("pagehide", abort);
    for (const track of localTracks) if (!closed.has(track)) {
      closed.add(track); track.off?.("track-ended", trackEnded);
      try { track.stop(); } catch {} try { track.close(); } catch {}
    }
    if (client) {
      client.removeAllListeners();
      if (published) { published = false; try { await client.unpublish(localTracks); } catch {} }
      try { await client.leave(); } catch {}
    }
  };
  const abort = () => { leave().catch(() => {}); };
  const fail = async (err) => {
    if (!stopped) { state("ended"); onError(humanizePermissionError(err)); }
    await leave();
  };
  const trackEnded = () => { fail(new Error("Camera or microphone disconnected")).catch(() => {}); };
  const watchExpiry = () => {
    clearTimeout(expiryTimer);
    if (auth.expires_at) {
      const deadline = Math.min((auth.expires_at + 30) * 1000, auth.join_closes_at ? new Date(auth.join_closes_at).getTime() : Infinity);
      expiryTimer = setTimeout(() => fail(new Error("Meeting credentials expired. Reopen the meeting to reconnect.")), Math.max(0, deadline - Date.now()));
    }
  };
  signal?.addEventListener("abort", abort, {once: true}); globalThis.addEventListener?.("pagehide", abort);
  try {
    check(); state("connecting"); auth = await fetchToken("meeting", channel); check();
    if (auth.role !== "host") throw new Error("Invalid private meeting authorization");
    watchExpiry();
    client = AgoraRTC.createClient({mode: "rtc", codec: "vp8"});
    remote = subscribeRemoteUsers(client, users => { if (!stopped) onUsers(users); });
    const renew = (expired = false) => {
      if (stopped) return;
      if (renewal) { expiredPending ||= expired; return renewal; }
      renewal = (async () => {
        const next = await api.agoraToken({flow: "meeting", channel: auth.channel, meeting_id: auth.meeting_id,
          uid: auth.uid, renewal_key: auth.renewal_key}); check();
        if (next.uid !== auth.uid || next.channel !== auth.channel || next.role !== "host") throw new Error("Meeting authorization changed");
        if (expired) {
          state("reconnecting"); remote.clear(); await client.leave(); check();
          await client.join(next.app_id, next.channel, next.token, next.uid); check();
          await client.publish(localTracks); published = true; check();
        } else { await client.renewToken(next.token); check(); }
        auth = next; watchExpiry(); remote.reconcile(); state("connected");
      })().catch(fail).finally(() => { renewal = null; if (expiredPending && !stopped) { expiredPending = false; renew(true); } });
      return renewal;
    };
    client.on("token-privilege-will-expire", () => renew());
    client.on("token-privilege-did-expire", () => renew(true));
    client.on("connection-state-change", current => {
      if (stopped) return;
      state(current === "CONNECTED" ? "connected" : "reconnecting");
      if (current === "CONNECTED") remote.reconcile(); else remote.clear();
    });
    client.on("media-reconnect-start", () => state("reconnecting"));
    client.on("media-reconnect-end", () => { remote.reconcile(); state("connected"); });
    await client.join(auth.app_id, auth.channel, auth.token, auth.uid); check();
    localTracks = await AgoraRTC.createMicrophoneAndCameraTracks({AEC: true, ANS: true}, {encoderConfig: "480p_1"}); check();
    for (const track of localTracks) track.on?.("track-ended", trackEnded);
    await client.publish(localTracks); published = true; check(); remote.reconcile(); state("connected");
    const validate = async () => {
      if (stopped) return;
      try {
        const row = await api.getMeeting(auth.meeting_id || channel.slice("meeting-".length)); check();
        if (row.status !== "confirmed" || row.room_version !== auth.room_version ||
            auth.join_closes_at && Date.now() >= new Date(auth.join_closes_at).getTime()) {
          throw new Error("This meeting has ended, been cancelled, or has a new proposed time");
        }
      } catch (err) {
        if (err.status === 429 || err.status >= 500 || err instanceof TypeError) state("reconnecting");
        else { await fail(err); return; }
      }
      if (!stopped) timer = setTimeout(validate, 15000);
    };
    timer = setTimeout(validate, 15000);
    return {client, localTracks, role: "host", leave};
  } catch (err) { await leave(); throw err; }
}

async function teardown(session) {
  try {
    if (session.localTracks) {
      for (const t of session.localTracks) {
        try { t.stop(); } catch {}
        try { t.close(); } catch {}
      }
      try { await session.client.unpublish(session.localTracks); } catch {}
    }
    try { await session.client.leave(); } catch {}
    try { session.client.removeAllListeners(); } catch {}
  } catch {
    /* swallow — we're leaving anyway */
  }
}

/** Attach handlers that mirror remote users into a state setter. */
export function subscribeRemoteUsers(client, onUsersChange) {
  const users = new Map();
  const versions = new Map(), pending = new Set();
  let disposed = false;
  const emit = () => { if (!disposed) onUsersChange(Array.from(users.values())); };

  const handleJoined = async (user, mediaType) => {
    if (disposed || !["audio", "video"].includes(mediaType)) return;
    const key = `${user.uid}:${mediaType}`;
    if (pending.has(key) || users.get(user.uid)?.[`${mediaType}Track`]) return;
    const version = (versions.get(key) || 0) + 1;
    versions.set(key, version); pending.add(key);
    try {
      await client.subscribe(user, mediaType);
    } catch {
      // subscribe can throw if the remote unpublishes mid-flight
      return;
    } finally { if (versions.get(key) === version) pending.delete(key); }
    if (disposed || versions.get(key) !== version) return;
    const existing = users.get(user.uid) || { uid: user.uid };
    if (mediaType === "video") existing.videoTrack = user.videoTrack;
    if (mediaType === "audio") {
      existing.audioTrack = user.audioTrack;
      try { user.audioTrack.play(); } catch {}
    }
    users.set(user.uid, existing);
    emit();
  };
  const handleLeft = (user, mediaType) => {
    for (const type of mediaType ? [mediaType] : ["audio", "video"]) {
      const key = `${user.uid}:${type}`; versions.set(key, (versions.get(key) || 0) + 1); pending.delete(key);
      try { users.get(user.uid)?.[`${type}Track`]?.stop(); } catch {}
    }
    const existing = users.get(user.uid);
    if (!existing) return;
    if (!mediaType || mediaType === "video") existing.videoTrack = null;
    if (!mediaType || mediaType === "audio") existing.audioTrack = null;
    if (!existing.videoTrack && !existing.audioTrack) users.delete(user.uid);
    else users.set(user.uid, existing);
    emit();
  };
  client.on("user-published", handleJoined);
  client.on("user-unpublished", handleLeft);
  const left = user => handleLeft(user);
  client.on("user-left", left);
  const dispose = () => {
    disposed = true;
    client.off("user-published", handleJoined);
    client.off("user-unpublished", handleLeft);
    client.off("user-left", left);
    for (const user of users.values()) for (const type of ["audioTrack", "videoTrack"]) { try { user[type]?.stop(); } catch {} }
    users.clear(); pending.clear();
  };
  dispose.clear = () => { for (const user of client.remoteUsers || []) handleLeft(user); };
  dispose.reconcile = () => {
    if (disposed) return;
    const current = client.remoteUsers || [];
    for (const uid of users.keys()) if (!current.some(u => u.uid === uid)) handleLeft({uid});
    for (const user of current) {
      if (user.hasVideo) handleJoined(user, "video");
      if (user.hasAudio) handleJoined(user, "audio");
    }
  };
  return dispose;
}

export function humanizePermissionError(err) {
  const message = String(err?.message || err || "");
  if (/NotAllowedError|PERMISSION_DENIED|Permission denied/i.test(message)) {
    return "Camera / microphone permission was denied. Please allow access in your browser and try again.";
  }
  if (/NotFoundError|DEVICES_NOT_FOUND/i.test(message)) {
    return "No camera or microphone was found on this device.";
  }
  if (/CAN_NOT_GET_GATEWAY_SERVER|NETWORK|network/i.test(message)) {
    return "Couldn't reach the video service. Check your connection and try again.";
  }
  return message || "Something went wrong connecting to the video service.";
}
